import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Building2,
  ChevronRight,
  ChevronDown,
  Loader2,
  Pencil,
  RefreshCw,
  GitBranch,
  Network,
  Landmark,
  LayoutGrid,
  Briefcase,
  AppWindow,
  List,
  GitFork,
  Trash2,
  AlertTriangle,
  MoreVertical,
} from "lucide-react";
import { getAPI, postAPI, deleteAPI, patchAPI } from "../../api/frameworksApi";
import { getErrorMessage } from "../../api/errorMap";
import { useToastStore } from "../../stores/toastStore";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { API_URL } from "../../constants";
import { validateMaxLength, validateRequired } from "../../utils/validators";
import {
  Modal,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

/* ── Types ─────────────────────────────────────────────── */

type OrgType =
  | "GROUP"
  | "ORGANIZATION"
  | "DIVISION"
  | "DEPARTMENT"
  | "APPLICATION";

const ORG_TYPES: { value: OrgType; label: string }[] = [
  { value: "GROUP", label: "Group" },
  { value: "ORGANIZATION", label: "Organization" },
  { value: "DIVISION", label: "Division" },
  { value: "DEPARTMENT", label: "Department" },
  { value: "APPLICATION", label: "Application" },
];

// Parent → child mapping (for hover "Add child" affordance)
const CHILD_TYPE_MAP: Record<OrgType, OrgType | null> = {
  GROUP: "ORGANIZATION",
  ORGANIZATION: "DIVISION",
  DIVISION: "DEPARTMENT",
  DEPARTMENT: "APPLICATION",
  APPLICATION: null,
};

// Child → required parent type. null = can be root (no parent).
// ORGANIZATION can be either — under a GROUP or as root.
const PARENT_TYPE_FOR: Record<OrgType, OrgType | null> = {
  GROUP: null,
  ORGANIZATION: "GROUP",
  DIVISION: "ORGANIZATION",
  DEPARTMENT: "DIVISION",
  APPLICATION: "DEPARTMENT",
};

// Whether a type can exist with no parent (root)
const CAN_BE_ROOT: Record<OrgType, boolean> = {
  GROUP: true,
  ORGANIZATION: true,
  DIVISION: false,
  DEPARTMENT: false,
  APPLICATION: false,
};

const TYPE_LABELS: Record<OrgType, string> = {
  GROUP: "Group",
  ORGANIZATION: "Organization",
  DIVISION: "Division",
  DEPARTMENT: "Department",
  APPLICATION: "Application",
};

interface OrgUnit {
  id: string;
  name: string;
  shortName: string;
  description: string;
  parentId: string | null;
  path: string;
  orgType: OrgType;
  isActive: boolean;
  children: OrgUnit[];
}

interface OrgForm {
  name: string;
  shortName: string;
  description: string;
  orgType: OrgType;
}

const EMPTY_FORM: OrgForm = {
  name: "",
  shortName: "",
  description: "",
  orgType: "ORGANIZATION",
};

/* ── Helpers ───────────────────────────────────────────── */

function flattenOrgs(nodes: OrgUnit[]): OrgUnit[] {
  const out: OrgUnit[] = [];
  const walk = (list: OrgUnit[]) =>
    list.forEach((n) => {
      out.push(n);
      walk(n.children ?? []);
    });
  walk(nodes);
  return out;
}

/* ── Component ─────────────────────────────────────────── */

export function OrganizationManagement() {
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [loading, setLoading] = useState(true);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<OrgForm>(EMPTY_FORM);
  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<OrgForm>(EMPTY_FORM);
  const [editId, setEditId] = useState("");
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Tree expand state
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // View mode
  const [viewMode, setViewMode] = useState<"tree" | "chart">("tree");

  const hasRoot = orgs.length > 0;
  const flatOrgs = useMemo(() => flattenOrgs(orgs), [orgs]);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAPI(
        API_URL.ORG_UNITS + "?root_only=true&flat=false",
      );
      setOrgs(res?.data ?? []);
      if (res?.data?.length) setExpanded(new Set([res.data[0].id]));
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to load organization units");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = (nodes: OrgUnit[]) => {
    const ids = new Set<string>();
    flattenOrgs(nodes).forEach((n) => ids.add(n.id));
    setExpanded(ids);
  };

  const collapseAll = () => setExpanded(new Set());

  // The button toggles to "Collapse all" only once every expandable node
  // (one with children) is already open — otherwise it keeps offering
  // "Expand all". Nodes without children don't count, so a fully-leafed
  // tree is treated as already collapsed.
  const expandableIds = useMemo(
    () =>
      flatOrgs
        .filter((n) => (n.children?.length ?? 0) > 0)
        .map((n) => n.id),
    [flatOrgs],
  );
  const isAllExpanded =
    expandableIds.length > 0 && expandableIds.every((id) => expanded.has(id));

  /* ── Create flow (unified Add Unit) ── */

  // Default type when opening modal: ORGANIZATION if a GROUP exists, otherwise GROUP if user wants;
  // but keep it simple — if no roots exist, prefer ORGANIZATION (most common as root).
  const openCreate = () => {
    setCreateForm({
      ...EMPTY_FORM,
      orgType: hasRoot ? "ORGANIZATION" : "ORGANIZATION",
    });
    setCreateParentId(null);
    setCreateErrors({});
    setShowCreate(true);
  };

  // Contextual "Add Child" — prefills type + parent
  const openCreateChild = (parent: OrgUnit) => {
    const childType = CHILD_TYPE_MAP[parent.orgType];
    if (!childType) return;
    setCreateForm({ ...EMPTY_FORM, orgType: childType });
    setCreateParentId(parent.id);
    setCreateErrors({});
    setShowCreate(true);
  };

  const onTypeChange = (next: OrgType) => {
    // Auto-clear parent if it's no longer valid for the new type
    setCreateForm((f) => ({ ...f, orgType: next }));
    const requiredParentType = PARENT_TYPE_FOR[next];
    if (createParentId) {
      const current = flatOrgs.find((o) => o.id === createParentId);
      if (!current || current.orgType !== requiredParentType) {
        setCreateParentId(null);
      }
    }
  };

  // Valid parent options for the currently selected type
  const validParentOptions = useMemo(() => {
    const t = createForm.orgType;
    const requiredType = PARENT_TYPE_FOR[t];
    if (requiredType === null) return []; // GROUP — never has a parent
    return flatOrgs.filter((o) => o.orgType === requiredType);
  }, [flatOrgs, createForm.orgType]);

  // Show the parent field only when valid parents exist for the chosen type.
  // Hidden for GROUP always (no parent type). Hidden for types that allow root if no parents exist.
  const showParentField =
    PARENT_TYPE_FOR[createForm.orgType] !== null &&
    validParentOptions.length > 0;
  // Block creation if the type strictly requires a parent but none exists yet.
  const noValidParents =
    PARENT_TYPE_FOR[createForm.orgType] !== null &&
    !CAN_BE_ROOT[createForm.orgType] &&
    validParentOptions.length === 0;

  const handleCreate = async () => {
    const errs: Record<string, string> = {};

    // Validate name
    const nameVal = validateRequired(createForm.name, "Name");
    if (!nameVal.valid) {
      errs.name = nameVal.error || "";
    } else {
      const nameLenVal = validateMaxLength(createForm.name.trim(), 255, "Name");
      if (!nameLenVal.valid) {
        errs.name = nameLenVal.error || "";
      }
    }

    // Validate short name
    const shortNameVal = validateRequired(createForm.shortName, "Short name");
    if (!shortNameVal.valid) {
      errs.shortName = shortNameVal.error || "";
    } else if (createForm.shortName.trim().length > 8) {
      errs.shortName = "Short name must not exceed 8 characters";
    }

    // Validate description
    if (createForm.description.trim()) {
      const descVal = validateMaxLength(
        createForm.description.trim(),
        1000,
        "Description",
      );
      if (!descVal.valid) {
        errs.description = descVal.error || "";
      }
    }

    // Validate parent selection
    if (showParentField && !createParentId) {
      errs.parent = "Parent unit is required for this type";
    }

    if (Object.keys(errs).length > 0) {
      setCreateErrors(errs);
      return;
    }

    if (noValidParents) return;
    setCreating(true);
    try {
      await postAPI(API_URL.ORG_UNITS, {
        name: createForm.name.trim(),
        shortName: createForm.shortName.trim(),
        description: createForm.description.trim(),
        parentId: createParentId,
        orgType: createForm.orgType,
      });
      setShowCreate(false);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `${TYPE_LABELS[createForm.orgType]} "${createForm.name}" created`,
        );
      await fetchOrgs();
      if (createParentId)
        setExpanded((prev) => new Set(prev).add(createParentId));
    } catch (err: any) {
      // 403 is already surfaced by the global axios interceptor with a
      // permission-specific "Access Denied" toast — skip the local toast
      // to avoid a duplicate generic "Error" message.
      if (err?.response?.status !== 403) {
        useToastStore
          .getState()
          .showError("Error", getErrorMessage(err, "Failed to create unit"));
      }
    } finally {
      setCreating(false);
    }
  };

  /* ── Edit / Delete ── */

  const openEdit = (org: OrgUnit) => {
    setEditForm({
      name: org.name,
      shortName: org.shortName,
      description: org.description || "",
      orgType: org.orgType || "DEPARTMENT",
    });
    setEditId(org.id);
    setEditErrors({});
    setShowEdit(true);
  };

  const handleEdit = async () => {
    const errs: Record<string, string> = {};

    // Validate name
    const nameVal = validateRequired(editForm.name, "Name");
    if (!nameVal.valid) {
      errs.name = nameVal.error || "";
    } else {
      const nameLenVal = validateMaxLength(editForm.name.trim(), 255, "Name");
      if (!nameLenVal.valid) {
        errs.name = nameLenVal.error || "";
      }
    }

    // Validate short name
    const shortNameVal = validateRequired(editForm.shortName, "Short name");
    if (!shortNameVal.valid) {
      errs.shortName = shortNameVal.error || "";
    } else if (editForm.shortName.trim().length > 8) {
      errs.shortName = "Short name must not exceed 8 characters";
    }

    // Validate description
    if (editForm.description.trim()) {
      const descVal = validateMaxLength(
        editForm.description.trim(),
        1000,
        "Description",
      );
      if (!descVal.valid) {
        errs.description = descVal.error || "";
      }
    }

    if (Object.keys(errs).length > 0) {
      setEditErrors(errs);
      return;
    }

    setEditing(true);
    try {
      await patchAPI(`${API_URL.ORG_UNITS}/${editId}`, {
        name: editForm.name.trim(),
        shortName: editForm.shortName.trim(),
        description: editForm.description.trim(),
        orgType: editForm.orgType,
      });
      setShowEdit(false);
      useToastStore
        .getState()
        .showSuccess("Success", `Unit "${editForm.name}" updated`);
      await fetchOrgs();
    } catch (err: any) {
      if (err?.response?.status !== 403) {
        useToastStore
          .getState()
          .showError("Error", getErrorMessage(err, "Failed to update unit"));
      }
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async (org: OrgUnit) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${org.name}"? This action cannot be undone.`,
      )
    )
      return;
    setDeletingId(org.id);
    try {
      await deleteAPI(`${API_URL.ORG_UNITS}/${org.id}`);
      useToastStore
        .getState()
        .showSuccess("Success", `Unit "${org.name}" deleted`);
      await fetchOrgs();
    } catch (err: any) {
      if (err?.response?.status !== 403) {
        useToastStore
          .getState()
          .showError("Error", getErrorMessage(err, "Failed to delete unit"));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const countAll = (nodes: OrgUnit[]): number =>
    nodes.reduce((sum, n) => sum + 1 + countAll(n.children || []), 0);

  return (
    <div className="space-y-6">
      {/* ── Page Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Organization Units</h1>
          <p className="text-sm d mt-0.5">
            Manage organizational hierarchy for access control and compliance
            scoping
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <button
            onClick={fetchOrgs}
            disabled={loading}
            className="h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-background d hover:text-foreground hover:bg-accent transition-all disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <RBACAction module="organization" action="create">
            <button
              onClick={openCreate}
              className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
            >
              <Plus className="size-4" /> Add Unit
            </button>
          </RBACAction>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-5 animate-spin d" />
        </div>
      ) : !hasRoot ? (
        /* Empty state */
        <div className="rounded-lg border border-border bg-card">
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="size-14 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <Building2 className="size-7 d" />
            </div>
            <h3 className="text-sm font-semibold mb-1">
              No organization unit created
            </h3>
            <p className="text-sm d mb-5 max-w-sm">
              Create your first unit to get started. You can add nested units
              under it later.
            </p>
            <button
              onClick={openCreate}
              className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
            >
              <Plus className="size-4" /> Add Unit
            </button>
          </div>
        </div>
      ) : (
        /* Hierarchy */
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
            <div className="flex items-center gap-2">
              <GitBranch className="size-4 d" />
              <h3 className="text-sm font-semibold">Hierarchy</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted d font-medium">
                {countAll(orgs)} unit{countAll(orgs) !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* View mode toggle */}
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted/50 border border-border/50">
                <button
                  onClick={() => setViewMode("tree")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    viewMode === "tree"
                      ? "bg-background text-foreground shadow-sm"
                      : "d hover:text-foreground"
                  }`}
                  title="Manage units"
                >
                  <List className="size-3.5" />
                  Manage
                </button>
                <button
                  onClick={() => setViewMode("chart")}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    viewMode === "chart"
                      ? "bg-background text-foreground shadow-sm"
                      : "d hover:text-foreground"
                  }`}
                  title="View organization chart"
                >
                  <GitFork className="size-3.5 rotate-180" />
                  View
                </button>
              </div>
              {viewMode === "tree" && (
                <button
                  onClick={() =>
                    isAllExpanded ? collapseAll() : expandAll(orgs)
                  }
                  className="text-xs d hover:text-foreground transition-colors"
                >
                  {isAllExpanded ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>
          </div>
          {viewMode === "tree" ? (
            <div className="p-4">
              {orgs.map((org) => (
                <TreeNode
                  key={org.id}
                  node={org}
                  depth={0}
                  expanded={expanded}
                  onToggle={toggleExpand}
                  onAddChild={openCreateChild}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                  isLast={true}
                />
              ))}
            </div>
          ) : (
            <div className="p-6 overflow-auto">
              <div className="inline-block min-w-full">
                <OrgChartView nodes={orgs} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Create Unit Modal ── */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        size="lg"
        title="Add Unit"
        description="Choose a unit type and where it lives in the hierarchy"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowCreate(false)}
            onSubmit={handleCreate}
            submitLabel={creating ? "Saving" : "Save"}
            submitDisabled={
              creating ||
              !createForm.name.trim() ||
              !createForm.shortName.trim() ||
              (showParentField && !createParentId) ||
              noValidParents ||
              Object.keys(createErrors).length > 0
            }
          />
        }
      >
        {noValidParents && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300 mb-3">
            <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              A {TYPE_LABELS[createForm.orgType]} must live under a{" "}
              {TYPE_LABELS[PARENT_TYPE_FOR[createForm.orgType]!]}, but none
              exist yet. Create a{" "}
              {TYPE_LABELS[PARENT_TYPE_FOR[createForm.orgType]!]} first.
            </p>
          </div>
        )}

        {/* Row 1: Type + Parent unit */}
        {showParentField ? (
          <div className="grid grid-cols-2 gap-x-5">
            <FormField label="Type" required>
              <FormSelect
                options={ORG_TYPES}
                value={createForm.orgType}
                onChange={(v) => onTypeChange(v as OrgType)}
              />
            </FormField>
            <FormField label="Parent unit" required error={createErrors.parent}>
              <FormSelect
                placeholder="Select parent"
                options={validParentOptions.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.shortName})`,
                }))}
                value={createParentId ?? ""}
                onChange={(v) => {
                  setCreateParentId(v || null);
                  if (createErrors.parent)
                    setCreateErrors((e) => ({ ...e, parent: "" }));
                }}
              />
            </FormField>
          </div>
        ) : (
          <FormField
            label="Type"
            required
            hint={
              CAN_BE_ROOT[createForm.orgType]
                ? `Will be created as a root ${TYPE_LABELS[createForm.orgType].toLowerCase()}`
                : undefined
            }
          >
            <FormSelect
              options={ORG_TYPES}
              value={createForm.orgType}
              onChange={(v) => onTypeChange(v as OrgType)}
            />
          </FormField>
        )}

        {/* Row 2: Name + Short Name */}
        <div className="grid grid-cols-2 gap-x-5">
          <FormField label="Name" required error={createErrors.name}>
            <FormInput
              placeholder={`e.g. ${createForm.orgType === "DEPARTMENT" ? "Engineering Department" : createForm.orgType === "APPLICATION" ? "HR System" : "Acme Corp"}`}
              value={createForm.name}
              onChange={(v) => {
                setCreateForm((f) => ({ ...f, name: v }));
                if (createErrors.name)
                  setCreateErrors((e) => ({ ...e, name: "" }));
              }}
            />
          </FormField>
          <FormField
            label="Short Name"
            required
            hint="Up to 8 characters"
            error={createErrors.shortName}
          >
            <FormInput
              placeholder="e.g. ENG"
              value={createForm.shortName}
              onChange={(v) => {
                setCreateForm((f) => ({ ...f, shortName: v }));
                if (createErrors.shortName)
                  setCreateErrors((e) => ({ ...e, shortName: "" }));
              }}
              maxLength={8}
            />
          </FormField>
        </div>

        {/* Row 3: Description */}
        <FormField label="Description" error={createErrors.description}>
          <FormTextarea
            placeholder="Brief description of this unit"
            value={createForm.description}
            onChange={(v) => {
              setCreateForm((f) => ({ ...f, description: v }));
              if (createErrors.description)
                setCreateErrors((e) => ({ ...e, description: "" }));
            }}
          />
        </FormField>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        open={showEdit}
        onClose={() => setShowEdit(false)}
        title="Edit Unit"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowEdit(false)}
            onSubmit={handleEdit}
            submitLabel={editing ? "Saving" : "Save"}
            submitDisabled={
              editing ||
              !editForm.name.trim() ||
              !editForm.shortName.trim() ||
              Object.keys(editErrors).length > 0
            }
          />
        }
      >
        <FormField label="Name" required error={editErrors.name}>
          <FormInput
            placeholder="e.g. Engineering Department"
            value={editForm.name}
            onChange={(v) => {
              setEditForm((f) => ({ ...f, name: v }));
              if (editErrors.name) setEditErrors((e) => ({ ...e, name: "" }));
            }}
          />
        </FormField>
        <FormField
          label="Short Name"
          required
          hint="Up to 8 characters"
          error={editErrors.shortName}
        >
          <FormInput
            placeholder="e.g. ENG"
            value={editForm.shortName}
            onChange={(v) => {
              setEditForm((f) => ({ ...f, shortName: v }));
              if (editErrors.shortName)
                setEditErrors((e) => ({ ...e, shortName: "" }));
            }}
            maxLength={8}
          />
        </FormField>
        <FormField label="Type">
          <FormSelect
            options={ORG_TYPES}
            value={editForm.orgType}
            onChange={(v) =>
              setEditForm((f) => ({ ...f, orgType: v as OrgType }))
            }
          />
        </FormField>
        <FormField label="Description" error={editErrors.description}>
          <FormTextarea
            placeholder="Brief description of this unit"
            value={editForm.description}
            onChange={(v) => {
              setEditForm((f) => ({ ...f, description: v }));
              if (editErrors.description)
                setEditErrors((e) => ({ ...e, description: "" }));
            }}
          />
        </FormField>
      </Modal>
    </div>
  );
}

/* ── Depth accent colors ── */

const DEPTH_STYLES = [
  {
    bg: "bg-indigo-50 dark:bg-indigo-500/10",
    border: "border-indigo-200 dark:border-indigo-500/20",
    icon: "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400",
    accent: "bg-indigo-500",
  },
  {
    bg: "bg-violet-50/60 dark:bg-violet-500/[0.06]",
    border: "border-violet-200/70 dark:border-violet-500/15",
    icon: "bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400",
    accent: "bg-violet-500",
  },
  {
    bg: "bg-sky-50/60 dark:bg-sky-500/[0.06]",
    border: "border-sky-200/70 dark:border-sky-500/15",
    icon: "bg-sky-100 dark:bg-sky-500/20 text-sky-600 dark:text-sky-400",
    accent: "bg-sky-500",
  },
  {
    bg: "bg-teal-50/60 dark:bg-teal-500/[0.06]",
    border: "border-teal-200/70 dark:border-teal-500/15",
    icon: "bg-teal-100 dark:bg-teal-500/20 text-teal-600 dark:text-teal-400",
    accent: "bg-teal-500",
  },
  {
    bg: "bg-amber-50/60 dark:bg-amber-500/[0.06]",
    border: "border-amber-200/70 dark:border-amber-500/15",
    icon: "bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400",
    accent: "bg-amber-500",
  },
];

const getDepthStyle = (depth: number) =>
  DEPTH_STYLES[depth % DEPTH_STYLES.length];

/* ── Descendant counts (recursive, by type) ─────────────── */

function countDescendantsByType(node: OrgUnit): Record<OrgType, number> {
  const counts: Record<OrgType, number> = {
    GROUP: 0,
    ORGANIZATION: 0,
    DIVISION: 0,
    DEPARTMENT: 0,
    APPLICATION: 0,
  };
  const walk = (n: OrgUnit) => {
    for (const child of n.children ?? []) {
      counts[child.orgType] += 1;
      walk(child);
    }
  };
  walk(node);
  return counts;
}

const HIERARCHY_ORDER: OrgType[] = [
  "GROUP",
  "ORGANIZATION",
  "DIVISION",
  "DEPARTMENT",
  "APPLICATION",
];

// For a given parent type, return all descendant types in hierarchy order
const descendantTypesFor = (parent: OrgType): OrgType[] =>
  HIERARCHY_ORDER.slice(HIERARCHY_ORDER.indexOf(parent) + 1);

const COUNT_ICONS: Record<OrgType, typeof Network> = {
  GROUP: Network,
  ORGANIZATION: Landmark,
  DIVISION: LayoutGrid,
  DEPARTMENT: Briefcase,
  APPLICATION: AppWindow,
};

/* ── Tree Node ─────────────────────────────────────────── */

function TreeNode({
  node,
  depth,
  expanded,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
  deletingId,
  isLast,
}: {
  node: OrgUnit;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAddChild: (org: OrgUnit) => void;
  onEdit: (org: OrgUnit) => void;
  onDelete: (org: OrgUnit) => void;
  deletingId: string | null;
  isLast: boolean;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = expanded.has(node.id);
  const isRoot = depth === 0;
  const style = getDepthStyle(depth);

  return (
    <div className={`relative ${isRoot ? "" : "ml-6"}`}>
      {/* Vertical connector */}
      {!isRoot && (
        <div
          className="absolute left-0 top-0 w-px bg-border dark:bg-white/[0.08]"
          style={{ height: isLast ? "20px" : "100%" }}
        />
      )}
      {!isRoot && (
        <div
          className="absolute left-0 top-5 h-px bg-border dark:bg-white/[0.08]"
          style={{ width: "24px" }}
        />
      )}

      {/* Node card */}
      <div
        className={`group relative rounded-lg border transition-all ${style.border} ${style.bg} hover:shadow-sm ${isRoot ? "" : "ml-6"}`}
        style={{ marginTop: isRoot ? 0 : 8, marginBottom: 4 }}
      >
        <div
          className={`absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-full ${style.accent} opacity-60`}
        />

        <div className="flex items-center gap-3 px-4 py-2.5">
          {hasChildren ? (
            <button
              onClick={() => onToggle(node.id)}
              className="flex-shrink-0 size-6 rounded-md flex items-center justify-center d hover:text-foreground hover:bg-background/60 dark:hover:bg-white/[0.06] transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="size-3.5" />
              ) : (
                <ChevronRight className="size-3.5" />
              )}
            </button>
          ) : (
            <span className="flex-shrink-0 size-6" />
          )}

          <div
            className={`size-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.icon}`}
          >
            {node.orgType === "GROUP" ? (
              <Network className="size-4" />
            ) : node.orgType === "ORGANIZATION" ? (
              <Landmark className="size-4" />
            ) : node.orgType === "DIVISION" ? (
              <LayoutGrid className="size-4" />
            ) : node.orgType === "APPLICATION" ? (
              <AppWindow className="size-4" />
            ) : (
              <Briefcase className="size-4" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold truncate">
                {node.name}
              </span>
              <span
                className="text-[11px] w-18 text-center px-1 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 font-medium border border-amber-200/40 dark:border-amber-500/15 truncate"
                title={node.shortName}
              >
                {node.shortName}
              </span>
              {node.orgType && (
                <span className="text-[10px] w-24 text-center px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-medium flex-shrink-0 truncate">
                  {node.orgType.charAt(0) + node.orgType.slice(1).toLowerCase()}
                </span>
              )}
              {!node.isActive && (
                <span className="text-[10px] w-16 text-center px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400 font-semibold flex-shrink-0">
                  Inactive
                </span>
              )}
            </div>

            {node.description && (
              <p className="text-xs d mt-0.5">{node.description}</p>
            )}
          </div>

          {/* Descendant counts (right-aligned, GitLab-style) */}
          {(() => {
            const types = descendantTypesFor(node.orgType);
            if (!types.length) return null;
            const counts = countDescendantsByType(node);
            return (
              <div className="hidden md:flex items-center gap-6 flex-shrink-0 ml-auto">
                {types.map((t) => {
                  const Icon = COUNT_ICONS[t];
                  const n = counts[t];
                  return (
                    <span
                      key={t}
                      className={`flex items-center gap-1 text-xs ${n > 0 ? "text-foreground/80" : "d/50"}`}
                      title={`${n} ${TYPE_LABELS[t]}${n === 1 ? "" : "s"}`}
                    >
                      <Icon className="size-4" />
                      <span className="font-semibold tabular-nums">{n}</span>
                    </span>
                  );
                })}
              </div>
            );
          })()}

          <div className="flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="p-1.5 rounded-md d hover:text-foreground hover:bg-background/60 dark:hover:bg-white/[0.08] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30 disabled:opacity-50"
                title="More actions"
                disabled={deletingId === node.id}
              >
                {deletingId === node.id ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <MoreVertical className="size-4" />
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {node.orgType !== "APPLICATION" && (
                  <>
                    <DropdownMenuItem onClick={() => onAddChild(node)}>
                      <Plus className="size-3.5" />
                      Add {TYPE_LABELS[CHILD_TYPE_MAP[node.orgType]!]}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <RBACAction module="organization" action="update">
                  <DropdownMenuItem onClick={() => onEdit(node)}>
                    <Pencil className="size-3.5" />
                    Edit details
                  </DropdownMenuItem>
                </RBACAction>
                <DropdownMenuSeparator />
                <RBACAction module="organization" action="delete">
                  <DropdownMenuItem
                    onClick={() => onDelete(node)}
                    variant="destructive"
                  >
                    <Trash2 className="size-3.5" />
                    Delete {TYPE_LABELS[node.orgType].toLowerCase()}
                  </DropdownMenuItem>
                </RBACAction>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className={`relative ${isRoot ? "ml-6" : "ml-6"}`}>
          {node.children.map((child, i) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              deletingId={deletingId}
              isLast={i === node.children.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Org Chart View ─────────────────────────────────────── */

function OrgChartView({ nodes }: { nodes: OrgUnit[] }) {
  if (!nodes.length) return null;
  return (
    <div className="flex justify-center items-start gap-8 min-w-max">
      {nodes.map((node) => (
        <OrgChartNode key={node.id} node={node} depth={0} />
      ))}
    </div>
  );
}

function OrgChartNode({ node, depth }: { node: OrgUnit; depth: number }) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const childCount = node.children?.length ?? 0;
  const style = getDepthStyle(depth);
  return (
    <div className="inline-flex flex-col items-center align-top">
      <div
        className={`relative rounded-md border transition-all hover:shadow-md ${style.border} ${style.bg} w-[200px]`}
      >
        <div
          className={`absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full ${style.accent} opacity-70`}
        />
        <div className="pl-3 pr-2 py-2 flex items-center gap-2">
          <div
            className={`size-6 rounded flex items-center justify-center flex-shrink-0 ${style.icon}`}
          >
            {node.orgType === "GROUP" ? (
              <Network className="size-3" />
            ) : node.orgType === "ORGANIZATION" ? (
              <Landmark className="size-3" />
            ) : node.orgType === "DIVISION" ? (
              <LayoutGrid className="size-3" />
            ) : node.orgType === "APPLICATION" ? (
              <AppWindow className="size-3" />
            ) : (
              <Briefcase className="size-3" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4
              className="text-xs font-semibold truncate leading-tight"
              title={node.name}
            >
              {node.name}
            </h4>
            <p className="text-[10px] d font-mono truncate leading-tight">
              {node.shortName}
            </p>
          </div>
        </div>
      </div>
      {hasChildren && <div className="w-px h-5 bg-border dark:bg-white/20" />}
      {hasChildren && (
        <div className="flex items-start">
          {node.children.map((child, i) => {
            const isFirst = i === 0;
            const isLast = i === childCount - 1;
            return (
              <div
                key={child.id}
                className="relative flex flex-col items-center px-3"
              >
                {childCount > 1 && (
                  <div
                    className="absolute top-0 h-px bg-border dark:bg-white/20"
                    style={{
                      left: isFirst ? "50%" : 0,
                      right: isLast ? "50%" : 0,
                    }}
                  />
                )}
                <div className="w-px h-5 bg-border dark:bg-white/20" />
                <OrgChartNode node={child} depth={depth + 1} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
